import { Box, Button, Flex, HStack, Text } from "@chakra-ui/react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { parseStakeMode, StakeMode } from "../../config/assets";
import { ThemeColor } from "../../theme/palette";
import { useStakeWithdrawalTicketsState } from "./StakeWithdrawalTicketsContext";

const TRACK_BG = "#0c1117";
const SEGMENT_ACTIVE_BG = "#1e2a3a";
const TEXT_MUTED = "whiteAlpha.700";

const MODES: { mode: StakeMode; label: string }[] = [
  { mode: "restake", label: "Stake" },
  { mode: "unstake", label: "Unstake" },
];

export const StakeModeTabs = () => {
  const { asset } = useParams<{ asset: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentMode = parseStakeMode(searchParams.get("mode"));
  const { count: pendingWithdrawals, isLoading: ticketsLoading } =
    useStakeWithdrawalTicketsState();

  const setMode = (mode: StakeMode) => {
    navigate(`/stake/${asset ?? "sol"}?mode=${mode}`);
  };

  return (
    <Box
      role="tablist"
      aria-label="Stake mode"
      bg={TRACK_BG}
      borderRadius="xl"
      border="1px solid"
      borderColor="whiteAlpha.200"
      p="3px"
      width="100%"
    >
      <Flex gap="3px">
        {MODES.map(({ mode, label }) => {
          const isActive = currentMode === mode;
          return (
            <Button
              key={mode}
              role="tab"
              type="button"
              aria-selected={isActive}
              flex={1}
              variant="unstyled"
              display="flex"
              alignItems="center"
              justifyContent="center"
              borderRadius="lg"
              py={2.5}
              px={3}
              minH="40px"
              bg={isActive ? SEGMENT_ACTIVE_BG : "transparent"}
              boxShadow={isActive ? "inset 0 0 0 1px rgba(95, 244, 244, 0.35)" : "none"}
              transition="background 0.2s ease, box-shadow 0.2s ease, color 0.2s ease"
              onClick={() => setMode(mode)}
              _hover={{
                bg: isActive ? SEGMENT_ACTIVE_BG : "whiteAlpha.100",
              }}
              _focusVisible={{
                boxShadow: "0 0 0 2px rgba(95, 244, 244, 0.5)",
              }}
            >
              <HStack spacing={1.5}>
                <Text
                  fontSize="sm"
                  fontWeight={isActive ? "semibold" : "medium"}
                  color={isActive ? ThemeColor.KAIROS : TEXT_MUTED}
                  letterSpacing="0.01em"
                >
                  {label}
                </Text>
                {mode === "unstake" && ticketsLoading && (
                  <Flex
                    align="center"
                    justify="center"
                    minW="18px"
                    h="18px"
                    borderRadius="full"
                    bg="whiteAlpha.200"
                    aria-label="Loading withdrawal count"
                  >
                    <Box
                      boxSize="6px"
                      borderRadius="full"
                      bg="whiteAlpha.500"
                    />
                  </Flex>
                )}
                {mode === "unstake" && !ticketsLoading && pendingWithdrawals > 0 && (
                  <Flex
                    align="center"
                    justify="center"
                    minW="18px"
                    h="18px"
                    px={1}
                    borderRadius="full"
                    bg={isActive ? ThemeColor.KAIROS : "whiteAlpha.300"}
                    color={isActive ? "#0c1117" : "white"}
                    fontSize="10px"
                    fontWeight="bold"
                    lineHeight="1"
                    aria-label={`${pendingWithdrawals} pending withdrawal requests`}
                  >
                    {pendingWithdrawals > 9 ? "9+" : pendingWithdrawals}
                  </Flex>
                )}
              </HStack>
            </Button>
          );
        })}
      </Flex>
    </Box>
  );
};
